import { isElementWithinTarget } from './common-functions';

/**
 * Listens for clicks anywhere on the document and determines if the item that was clicked is within the passed in
 * element.
 * @param element {HTMLElement} - The element to check for click events.
 * @param callback - The callback function used to tell the caller if the click was within the passed in element.
 * @param otherElements {Array<HTMLElement>} - If defined these other element will return true for isClickedWithin.
 * @param ignoredElements {Array<HTMLElement>} - If defined these elements will return false for isClickedWithin.
 * @return - The listener object that will be passed to the unbind function.
 */
export function bindDocumentClickListener(
  element: HTMLElement,
  callback: (isClickedWithin: boolean) => void,
  otherElements?: Array<HTMLElement>,
  ignoredElements?: Array<HTMLElement>
) {
  const clickListener = (event: MouseEvent) => {
    const targetElement = event.target as HTMLElement;
    // without this the element being checked will never show since the callback will always return false...this is not
    // true if we use transitions to determine when to bind and unbind the listener but I will leave this here since
    // that may not always be the case
    const isVisible = element.style.display !== 'hidden';

    if (
      element &&
      isVisible &&
      !isElementWithinTarget(element, targetElement) &&
      !isClickedInOtherElements(otherElements, targetElement)
    ) {
      callback(false);
    }

    const ignoreClicked = isClickedInOtherElements(ignoredElements, targetElement);
    callback(!ignoreClicked);
  };

  const isClickedInOtherElements = (elements: Array<HTMLElement> | undefined, targetElement: HTMLElement) => {
    if (!elements) {
      return false;
    }

    for (let i = 0, length = elements.length; i < length; i++) {
      if (isElementWithinTarget(elements[i], targetElement)) {
        return true;
      }
    }

    return false;
  };

  document.addEventListener('mousedown', clickListener);

  return clickListener;
}

/**
 * Removes the listener from the document.
 * @param clickListener - The listener created in the bind function.
 */
export function unbindDocumentClickListener(clickListener: (event: MouseEvent) => void) {
  document.removeEventListener('mousedown', clickListener);
}

export function bindDocumentKeyListener(keyNames: Array<string>, callback: (isKeyNamePressed: boolean) => void) {
  const keyListener = (event: KeyboardEvent) => {
    if (keyNames.includes(event.key)) {
      callback(true);
    } else {
      callback(false);
    }
  };

  document.addEventListener('keydown', keyListener);

  return keyListener;
}

export function unbindDocumentKeyListener(keyListener: (event: KeyboardEvent) => void) {
  document.removeEventListener('keydown', keyListener);
}
